import { StatusResponse } from '@/types/api';
import { platformClient } from '../../platform/api/platform.client';
import {
  GradeFeatureAllocationsResponse,
  PutGradeFeatureAllocationsPayload,
  School,
} from '../../platform/types';

export const getSchoolProfile = async (schoolId: string): Promise<School> => {
  const response = await platformClient.get<School>(`/schools/${schoolId}`);
  return response.data;
};

export interface UpdateSchoolProfilePayload {
  name?: string;
  code?: string | null;
  address?: string | null;
  timezone?: string | null;
  phone?: string | null;
  website?: string | null;
  description?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  board?: string | null;
  preferred_language?: string | null;
  branding?: {
    logo_url?: string | null;
    theme_color?: string | null;
  };
}

export const updateSchoolProfile = async (
  schoolId: string,
  payload: UpdateSchoolProfilePayload
): Promise<StatusResponse> => {
  const response = await platformClient.patch<StatusResponse>(`/schools/${schoolId}`, payload);
  return response.data;
};

/** Payload for PATCH /schools/:schoolId/school-settings (partial update). */
export interface PatchSchoolSettingsPayload {
  feature_flags?: { ai_tutor?: boolean; chat?: boolean; [key: string]: boolean | undefined };
  language?: { ui_default?: string; allowed?: string[] };
  academic_year?: { start_date?: string; end_date?: string; key?: string };
  notification_defaults?: { email_enabled?: boolean; [key: string]: unknown };
  security_policy?: { session_timeout_minutes?: number; [key: string]: unknown };
}

export const patchSchoolSettings = async (
  schoolId: string,
  payload: PatchSchoolSettingsPayload
): Promise<StatusResponse> => {
  const response = await platformClient.patch<StatusResponse>(
    `/schools/${schoolId}/school-settings`,
    payload
  );
  return response.data;
};

export const getGradeFeatureAllocations = async (
  schoolId: string
): Promise<GradeFeatureAllocationsResponse> => {
  const response = await platformClient.get<GradeFeatureAllocationsResponse>(
    `/schools/${schoolId}/grade-feature-allocations`
  );
  return response.data;
};

export const putGradeFeatureAllocations = async (
  schoolId: string,
  payload: PutGradeFeatureAllocationsPayload
): Promise<GradeFeatureAllocationsResponse> => {
  const response = await platformClient.put<GradeFeatureAllocationsResponse>(
    `/schools/${schoolId}/grade-feature-allocations`,
    payload
  );
  return response.data;
};

export const deleteGradeFeatureAllocations = async (schoolId: string): Promise<void> => {
  await platformClient.delete(`/schools/${schoolId}/grade-feature-allocations`);
};

export interface InvitePayload {
  email: string;
  invitee_name: string;
  notes?: string;
  metadata?: Record<string, any>;
  teacher_profile_id?: string;
}

export const inviteTeacher = async (
  schoolId: string,
  payload: InvitePayload
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(`/schools/${schoolId}/invites/teachers`, payload);
  return response.data;
};

export const inviteStudent = async (
  schoolId: string,
  payload: InvitePayload
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(`/schools/${schoolId}/invites/students`, payload);
  return response.data;
};

export interface CreatedInvite {
  id: string;
  email: string;
  status: string;
  expires_at?: string | null;
}

export const listCreatedInvites = async (
  schoolId: string,
  purpose: 'teacher' | 'student'
): Promise<CreatedInvite[]> => {
  const response = await platformClient.get<{ items: CreatedInvite[] }>(
    `/schools/${schoolId}/invites/created`,
    {
      params: { purpose },
    }
  );
  return response.data.items || [];
};
